import {Component, OnInit, ViewChild} from '@angular/core';

import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {
    PatientRegistrationAccountSettingsComponent
} from "@cdss/pages/patient-registration/account-settings/patient-registration-account-settings.component";
import {
    PatientRegistrationPersonalInfoComponent
} from "@cdss/pages/patient-registration/personal-info/patient-registration-personal-info.component";
import {NgClass, NgIf} from "@angular/common";
import {Patient} from "@shared/api/models/query/patient.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {
    PatientRegistrationEdAndComorbiditiesComponent
} from "@cdss/pages/patient-registration/ed-and-comorbidities/patient-registration-ed-and-comorbidities.component";
import {
    PatientRegistrationCareProgrammeComponent,
} from "@cdss/pages/patient-registration/care-programme/patient-registration-care-programme.component";
import {
    PatientRegistrationConfirmationComponent
} from "@cdss/pages/patient-registration/confirmation/patient-registration-confirmation.component";
import {DepartmentsService} from "@shared/api/services/v2/departments.service";
import {Department} from "@shared/api/models/query/department.model";
import {ServicesService} from "@shared/api/services/v2/services.service";
import {Service} from "@shared/api/models/query/service.model";
import {ToastrService} from "ngx-toastr";

@Component({
    selector: 'cdss-patient-edit-page',
    standalone: true,
    imports: [
        SpinnerComponent,
        PatientRegistrationAccountSettingsComponent,
        PatientRegistrationPersonalInfoComponent,
        NgIf,
        PatientRegistrationEdAndComorbiditiesComponent,
        NgClass,
        PatientRegistrationCareProgrammeComponent,
        RouterLink,
    ],
    templateUrl: './patient-edit.page.html',
    styleUrl: './patient-edit.page.scss'
})
export class PatientEditPage implements OnInit {
    @ViewChild(PatientRegistrationAccountSettingsComponent) accountSettingsComponent: PatientRegistrationAccountSettingsComponent
    @ViewChild(PatientRegistrationPersonalInfoComponent) personalInfoComponent: PatientRegistrationPersonalInfoComponent
    @ViewChild(PatientRegistrationEdAndComorbiditiesComponent) edAndComorbiditiesComponent: PatientRegistrationEdAndComorbiditiesComponent
    @ViewChild(PatientRegistrationCareProgrammeComponent) careProgrammeComponent: PatientRegistrationCareProgrammeComponent
    @ViewChild(PatientRegistrationConfirmationComponent) confirmationComponent: PatientRegistrationConfirmationComponent

    loading: boolean = false

    patientId?: number
    patient?: Patient
    department?: Department
    service?: Service

    organisationId: number
    serviceId: number
    departmentId: number
    typeOfUser: string
    licenseId: number

    step: number = 0

    constructor(
        private breadcrumbService: BreadcrumbService,
        private router: Router,
        private activeRoute: ActivatedRoute,
        private patientsService: PatientsService,
        private departmentsService: DepartmentsService,
        private servicesService: ServicesService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbService.set([
            {title: "Patient settings", url: null},
        ])
        this.patientId = this.activeRoute.snapshot.params["id"]
        if (this.patientId) {
            this.initPatient()
        }
    }

    async initPatient() {
        this.loading = true;
        this.patient = await this.patientsService.getById(this.patientId)
        this.departmentId = this.patient.department.id
        this.department = await this.departmentsService.getById(this.departmentId)
        this.serviceId = this.department.serviceDepartments[0].service.id
        this.service = await this.servicesService.getById(this.serviceId)
        this.organisationId = this.service.organisationServices[0].organisation.id
        this.licenseId = this.patient?.user?.activeLicense?.license?.id

        // UNIVAPLAT-2827: use the authoritative patient.type (as the registration page does
        // since UNIVAPLAT-1544) instead of inferring the type from phone-number presence.
        // The phone heuristic mis-derives the type (e.g. "Patient" for a "Patient and carer"
        // record without a patient phone), which hides the carer block (relation to patient),
        // queries the ED and co-morbidities form with the wrong target (no form results), and
        // skips the care programme blocks.
        if (this.patient.type) {
            this.typeOfUser = this.patient.type
        } else {
            // Fallback for patients registered before the type field was introduced
            const hasPatient = (this.patient?.user?.phoneNumber != null)
            const hasCarer = (this.patient.carer?.user?.phoneNumber != null)

            if (hasPatient && hasCarer) {
                this.typeOfUser = "Patient and carer"
            } else {
                if (hasCarer) {
                    this.typeOfUser = "Carer"
                } else {
                    this.typeOfUser = "Patient"
                }
            }
        }

        this.moveToStep(1)

        this.loading = false
    }

    moveToStep(step: number) {
        this.step = step
        switch (step) {
            case 1:
                setTimeout(() => {
                    this.accountSettingsComponent.init(this.typeOfUser, this.departmentId, this.serviceId, this.organisationId, this.licenseId, this.patient)
                }, 100)
                break
            case 2:
                setTimeout(() => {
                    this.personalInfoComponent.init(this.typeOfUser, this.departmentId, this.licenseId, null, false, this.patient)
                }, 100)
                break
            case 3:
                setTimeout(() => {
                    this.edAndComorbiditiesComponent.init(this.patient, this.typeOfUser)
                }, 100)
                break
            case 4:
                setTimeout(() => {
                    this.careProgrammeComponent.init(this.patient, this.typeOfUser, true, this.organisationId)
                }, 100)
                break
        }
    }

    update() {
        switch (this.step) {
            case 2:
                this.personalInfoComponent.processForm(false)
                break
            case 3:
                this.edAndComorbiditiesComponent.processForm(false)
                break
        }
    }

    updateCompleted() {
        switch (this.step) {
            case 3:
                this.toastr.success('User updated successfully', 'Success')
                break
        }
    }
}
